import { useEffect, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { usePageMeta } from "../usePageMeta";
import { snoozeBanner } from "../digitizingServiceStorage";
import { WITH_COOKIE } from "../tokenStorage";

const PAGE_TITLE = "Custom Embroidery Digitizing – Logo to Embroidery File | Embroidery Online";
const PAGE_DESCRIPTION =
  "Send us your logo or image and we'll hand-digitize it into a machine-ready embroidery file in PES, DST, EXP, JEF and more. Request a free quote.";

export interface DigitizingServiceConfig {
  price_from: string | null;
  // "24 hours", from DIGITIZING_TURNAROUND_HOURS, or null when unset.
  turnaround: string | null;
  max_upload_bytes: number;
  banner_snooze_hours: number;
}

const PLACEMENTS: { value: string; label: string }[] = [
  { value: "hat", label: "Hat" },
  { value: "shirt_chest", label: "Shirt or chest" },
  { value: "jacket_back", label: "Jacket back" },
  { value: "patch", label: "Patch" },
  { value: "towel_bag", label: "Towel or bag" },
  { value: "other", label: "Other" },
];

const FORMATS = ["pes", "dst", "exp", "jef", "other"];

// Checked here so a wrong file is caught before it uploads; the server
// checks again, by content, and is the one that counts.
const ACCEPTED_EXTENSIONS = ["png", "jpg", "jpeg", "svg", "pdf"];
const ACCEPT = ".png,.jpg,.jpeg,.svg,.pdf,image/png,image/jpeg,image/svg+xml,application/pdf";

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

// Analytics labels. Categories only, never anything typed or the file's
// name: the funnel needs to know what happened, not to whom.
function errorTypeForStatus(status: number): string {
  if (status === 429) return "rate_limited";
  if (status === 400 || status === 413 || status === 415 || status === 422) return "validation";
  return "server";
}

function fileTypeOf(file: File): string {
  const extension = extensionOf(file.name);
  if (extension === "jpeg") return "jpg";
  return ACCEPTED_EXTENSIONS.includes(extension) ? extension : "other";
}

function sizeBucketOf(file: File): string {
  const mb = file.size / (1024 * 1024);
  if (mb < 1) return "<1MB";
  if (mb < 5) return "1-5MB";
  if (mb <= 10) return "5-10MB";
  return ">10MB";
}

// Where the visitor came from: the banner and home card link with ?from=,
// anything else is told apart by whether there is a referrer at all.
function sourceOfVisit(): string {
  const from = new URLSearchParams(window.location.search).get("from");
  if (from === "banner" || from === "home" || from === "viewer") return from;
  return document.referrer ? "other" : "direct";
}

interface DigitizingServicePageProps {
  apiUrl: string;
  token: string;
  config: DigitizingServiceConfig | null;
}

/**
 * The quote request form for hand digitizing. A person quotes each request
 * by email, so this only collects what they need to, and says nothing about
 * price or turnaround that config doesn't.
 */
export function DigitizingServicePage({ apiUrl, token, config }: DigitizingServicePageProps) {
  usePageMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: "/digitizing-service" });
  const posthog = usePostHog();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sizeValue, setSizeValue] = useState("");
  const [sizeDimension, setSizeDimension] = useState("width");
  const [sizeUnit, setSizeUnit] = useState("in");
  const [placement, setPlacement] = useState("");
  const [formats, setFormats] = useState<string[]>([]);
  const [colors, setColors] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [notes, setNotes] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const errorRef = useRef<HTMLParagraphElement>(null);

  const maxBytes = config?.max_upload_bytes ?? 10 * 1024 * 1024;
  const maxMb = Math.round((maxBytes / (1024 * 1024)) * 10) / 10;

  const viewedFired = useRef(false);
  useEffect(() => {
    if (viewedFired.current) return;
    viewedFired.current = true;
    posthog?.capture("digitizing_page_viewed", { source: sourceOfVisit() });
    // Counted; now out of the address bar, so a reload or a shared link
    // isn't credited to the banner again.
    if (window.location.search) {
      window.history.replaceState(window.history.state, "", window.location.pathname);
    }
  }, [posthog]);

  // The funnel's middle: a first touch of any field, and leaving after one
  // without having sent the form. Refs, because the leave handler outlives
  // the render that installed it.
  const startedRef = useRef(false);
  const submittedRef = useRef(false);
  const exitFiredRef = useRef(false);
  const handleFormChange = (e: React.FormEvent<HTMLFormElement>) => {
    if (startedRef.current) return;
    if ((e.target as HTMLInputElement).name === "website") return;
    startedRef.current = true;
    posthog?.capture("digitizing_form_started");
  };
  useEffect(() => {
    const exit = () => {
      if (!startedRef.current || submittedRef.current || exitFiredRef.current) return;
      exitFiredRef.current = true;
      // A beacon, so a closing tab still gets it out.
      posthog?.capture("digitizing_page_exited_without_submit", {}, { transport: "sendBeacon" });
    };
    // pagehide covers closing the tab and leaving the site; the cleanup
    // covers navigating elsewhere in the app.
    window.addEventListener("pagehide", exit);
    return () => {
      window.removeEventListener("pagehide", exit);
      exit();
    };
  }, [posthog]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  // A failed submit, as the server or the network reported it.
  const fail = (message: string, errorType: string) => {
    setError(message);
    posthog?.capture("digitizing_form_failed", { error_type: errorType });
  };

  // Caught here, before anything was sent: the field's name only.
  const invalid = (message: string, field: string) => {
    setError(message);
    posthog?.capture("digitizing_validation_error", { field });
  };

  const rejectFile = (reason: "type" | "size") => {
    setError(
      reason === "type"
        ? "Please choose a PNG, JPG, SVG or PDF file."
        : `That file is larger than ${maxMb} MB. Please choose a smaller one.`,
    );
    posthog?.capture("digitizing_file_rejected", { reason });
  };

  // The client's own reason to refuse a file, if it has one; the server
  // checks again, by content.
  const fileProblem = (picked: File): "type" | "size" | null => {
    if (!ACCEPTED_EXTENSIONS.includes(extensionOf(picked.name))) return "type";
    if (picked.size > maxBytes) return "size";
    return null;
  };

  const handleFile = (picked: File | null) => {
    setError(null);
    setFile(picked);
    // An <img> renders an SVG without running anything in it, so all three
    // image types get a thumbnail. A PDF just gets its name.
    setPreviewUrl(picked && extensionOf(picked.name) !== "pdf" ? URL.createObjectURL(picked) : null);
    if (!picked) return;
    posthog?.capture("digitizing_file_selected", {
      file_type: fileTypeOf(picked),
      file_size_bucket: sizeBucketOf(picked),
    });
    const problem = fileProblem(picked);
    if (problem) rejectFile(problem);
  };

  // The browser's own required/min/max checks stop the submit before
  // handleSubmit runs; each field it flags says so here.
  const handleInvalid = (e: React.FormEvent<HTMLFormElement>) => {
    const field = (e.target as HTMLInputElement).name;
    if (field) posthog?.capture("digitizing_validation_error", { field });
  };

  const toggleFormat = (format: string) => {
    setFormats((current) =>
      current.includes(format) ? current.filter((f) => f !== format) : [...current, format],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // They have found the form and tried it: whatever happens next, the
    // banner pointing here can rest for a day.
    snoozeBanner();
    if (!file) return invalid("Please choose an image to upload.", "file");
    const problem = fileProblem(file);
    if (problem) return rejectFile(problem);
    if (formats.length === 0) return invalid("Please choose at least one file format.", "formats");

    const body = new FormData();
    body.append("file", file);
    body.append("email", email);
    body.append("name", name);
    body.append("size_value", sizeValue);
    body.append("size_unit", sizeUnit);
    body.append("size_dimension", sizeDimension);
    body.append("placement", placement);
    for (const format of formats) body.append("formats", format);
    body.append("colors", colors);
    body.append("needed_by", neededBy);
    body.append("notes", notes);
    body.append("website", website);
    if (token) body.append("token", token);

    setSubmitting(true);
    try {
      const response = await fetch(`${apiUrl}/api/digitizing-service/requests`, {
        ...WITH_COOKIE,
        method: "POST",
        body,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const detail = typeof data.detail === "string" ? data.detail : null;
        if (response.status === 413) posthog?.capture("digitizing_file_rejected", { reason: "size" });
        if (response.status === 415) posthog?.capture("digitizing_file_rejected", { reason: "type" });
        fail(
          detail ?? "Something went wrong sending your request. Please try again.",
          errorTypeForStatus(response.status),
        );
        return;
      }
      submittedRef.current = true;
      posthog?.capture("digitizing_form_submitted", {
        garment_type: placement,
        formats,
        file_type: fileTypeOf(file),
        has_deadline: Boolean(neededBy),
        has_notes: Boolean(notes.trim()),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch {
      fail("We couldn't reach the server. Check your connection and try again.", "network");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <section className="tool-intro digitizing-intro">
        <h1>Custom embroidery digitizing</h1>
        <p>Send us your logo or image and we'll digitize it by hand into a clean, machine-ready embroidery file.</p>
        <p>You get the file in the formats your machine reads: PES, DST, EXP, JEF and others.</p>
        {config?.turnaround && (
          <p className="digitizing-turnaround">
            <strong>Get your quote within {config.turnaround}.</strong>
          </p>
        )}
        <p>Tell us about the design below and we'll email you a quote. Nothing to pay now.</p>
        {config?.price_from && (
          <p>
            Prices from <strong>{config.price_from}</strong>.
          </p>
        )}
      </section>

      {submitted ? (
        <article className="digitizing-form" role="status">
          <h2>Thanks, we've got your request</h2>
          <p>
            We'll email you at <strong>{email}</strong> with your quote
            {config?.turnaround ? ` within ${config.turnaround}` : " soon"}. A confirmation is on
            its way to your inbox too.
          </p>
        </article>
      ) : (
        <form
          className="digitizing-form"
          onSubmit={handleSubmit}
          onChange={handleFormChange}
          onInvalidCapture={handleInvalid}
        >
          <label>
            Email
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label>
            Name <small>(optional)</small>
            <input
              type="text"
              name="name"
              autoComplete="name"
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label>
            Your logo or image
            <input
              type="file"
              name="file"
              accept={ACCEPT}
              required
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />
            <small>PNG, JPG, SVG or PDF, up to {maxMb} MB.</small>
          </label>
          {previewUrl && <img className="digitizing-preview" src={previewUrl} alt="Preview of your upload" />}
          {file && !previewUrl && (
            <p>
              <small>Selected: {file.name}</small>
            </p>
          )}

          <label htmlFor="digitizing-size">Approximate finished size</label>
          <div className="digitizing-size">
            <input
              id="digitizing-size"
              type="number"
              name="size_value"
              min="0.1"
              step="any"
              inputMode="decimal"
              required
              placeholder={sizeUnit === "in" ? "e.g. 3.5" : "e.g. 90"}
              value={sizeValue}
              onChange={(e) => setSizeValue(e.target.value)}
            />
            <select
              name="size_dimension"
              aria-label="Which side"
              value={sizeDimension}
              onChange={(e) => setSizeDimension(e.target.value)}
            >
              <option value="width">wide</option>
              <option value="height">tall</option>
            </select>
            <select name="size_unit" aria-label="Unit" value={sizeUnit} onChange={(e) => setSizeUnit(e.target.value)}>
              <option value="in">in</option>
              <option value="mm">mm</option>
            </select>
          </div>

          <label>
            What it's going on
            <select name="placement" required value={placement} onChange={(e) => setPlacement(e.target.value)}>
              <option value="" disabled>
                Choose one…
              </option>
              {PLACEMENTS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="digitizing-formats">
            <legend>File formats you need</legend>
            {FORMATS.map((format) => (
              <label key={format}>
                <input
                  type="checkbox"
                  name="formats"
                  value={format}
                  checked={formats.includes(format)}
                  onChange={() => toggleFormat(format)}
                />
                {format === "other" ? "Other" : format.toUpperCase()}
              </label>
            ))}
          </fieldset>

          <label>
            Number of colors <small>(optional)</small>
            <input
              type="number"
              name="colors"
              min="1"
              max="100"
              step="1"
              inputMode="numeric"
              value={colors}
              onChange={(e) => setColors(e.target.value)}
            />
          </label>

          <label>
            Needed by <small>(optional)</small>
            <input
              type="date"
              name="needed_by"
              min={today}
              value={neededBy}
              onChange={(e) => setNeededBy(e.target.value)}
            />
          </label>

          <label>
            Notes <small>(optional)</small>
            <textarea
              name="notes"
              rows={4}
              maxLength={5000}
              placeholder="Thread colors, fabric, anything we should know"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          {/* Honeypot: people never see or reach this; bots fill it in. */}
          <div className="digitizing-hp" aria-hidden="true">
            <label>
              Website
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </label>
          </div>

          {error && (
            <p ref={errorRef} tabIndex={-1} role="alert" style={{ color: "var(--pico-del-color)" }}>
              {error}
            </p>
          )}

          <div className="digitizing-submit">
            <button
              type="submit"
              aria-busy={submitting}
              disabled={submitting}
              onClick={() => posthog?.capture("digitizing_submit_clicked")}
            >
              {submitting ? "Sending…" : "Request a quote"}
            </button>
            {config?.turnaround && <span>Get your quote within {config.turnaround}.</span>}
          </div>
        </form>
      )}
    </>
  );
}
